import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { CHANGE_ALIGNMENT_REASON, INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { akEditorDefaultLayoutWidth, akEditorGutterPaddingDynamic, akEditorMobileBreakoutPoint } from '@atlaskit/editor-shared-styles';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { setTableAlignmentWithTableContentWithPosWithAnalytics } from '../pm-plugins/commands/commands-with-analytics';
import { getPluginState } from '../pm-plugins/plugin-factory';
import { TABLE_MAX_WIDTH, TABLE_OFFSET_IN_COMMENT_EDITOR } from '../pm-plugins/table-resizing/utils/consts';
import { ALIGN_CENTER, ALIGN_START } from '../pm-plugins/utils/alignment';
import { TableCssClassName as ClassName } from '../types';
import { getAlignmentStyle } from './table-container-styles';
import { TableResizer } from './TableResizer';
var InnerContainer = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    style = _ref.style,
    node = _ref.node,
    children = _ref.children;
  return /*#__PURE__*/React.createElement("div", {
    ref: ref
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: style
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: className,
    "data-number-column": node.attrs.isNumberColumnEnabled,
    "data-layout": node.attrs.layout,
    "data-testid": "table-container"
  }, children);
});
var AlignmentTableContainer = function AlignmentTableContainer(_ref2) {
  var node = _ref2.node,
    children = _ref2.children,
    pluginInjectionApi = _ref2.pluginInjectionApi,
    getPos = _ref2.getPos,
    editorView = _ref2.editorView;
  var alignment = node.attrs.layout !== ALIGN_START ? ALIGN_CENTER : ALIGN_START;
  var _useSharedPluginState = useSharedPluginState(pluginInjectionApi, ['table'], {
      disabled: editorExperiment('platform_editor_usesharedpluginstateselector', true)
    }),
    tableState = _useSharedPluginState.tableState;

  // isFullWidthModeEnabled
  var isTableFullWidthModeEnabledSelector = useSharedPluginStateSelector(pluginInjectionApi, 'table.isFullWidthModeEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isFullWidthModeEnabled = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isTableFullWidthModeEnabledSelector : tableState === null || tableState === void 0 ? void 0 : tableState.isFullWidthModeEnabled;

  // wasFullWidthModeEnabled
  var wasFullWidthModeEnabledSelector = useSharedPluginStateSelector(pluginInjectionApi, 'table.wasFullWidthModeEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var wasFullWidthModeEnabled = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? wasFullWidthModeEnabledSelector : tableState === null || tableState === void 0 ? void 0 : tableState.wasFullWidthModeEnabled;
  useEffect(function () {
    if (!tableState && editorExperiment('platform_editor_usesharedpluginstateselector', false)) {
      return;
    }
    if (editorView && getPos) {
      var state = editorView.state,
        dispatch = editorView.dispatch;
      if (wasFullWidthModeEnabled && isFullWidthModeEnabled !== undefined && !isFullWidthModeEnabled && alignment !== ALIGN_CENTER && node.attrs.width > akEditorDefaultLayoutWidth) {
        var _pluginInjectionApi$a;
        var pos = getPos && getPos();
        if (typeof pos !== 'number') {
          return;
        }
        setTableAlignmentWithTableContentWithPosWithAnalytics(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions)(ALIGN_CENTER, alignment, {
          pos: pos,
          node: node
        }, INPUT_METHOD.AUTO, CHANGE_ALIGNMENT_REASON.EDITOR_APPEARANCE_CHANGED)(state, dispatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorView, tableState, isFullWidthModeEnabled, wasFullWidthModeEnabled, node]);
  var style = useMemo(function () {
    return getAlignmentStyle(alignment);
  }, [alignment]);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    React.createElement("div", {
      "data-testid": "table-alignment-container",
      style: style
    }, children)
  );
};
var AlignmentTableContainerWrapper = function AlignmentTableContainerWrapper(_ref3) {
  var isTableAlignmentEnabled = _ref3.isTableAlignmentEnabled,
    node = _ref3.node,
    children = _ref3.children,
    pluginInjectionApi = _ref3.pluginInjectionApi,
    getPos = _ref3.getPos,
    editorView = _ref3.editorView;
  if (!isTableAlignmentEnabled) {
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": "table-alignment-container",
      style: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        display: 'flex',
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        justifyContent: 'center'
      }
    }, children);
  }
  return /*#__PURE__*/React.createElement(AlignmentTableContainer, {
    node: node,
    pluginInjectionApi: pluginInjectionApi,
    getPos: getPos,
    editorView: editorView
  }, children);
};
export var ResizableTableContainer = /*#__PURE__*/React.memo(function (_ref4) {
  var children = _ref4.children,
    className = _ref4.className,
    node = _ref4.node,
    containerWidth = _ref4.containerWidth,
    lineLength = _ref4.lineLength,
    editorView = _ref4.editorView,
    getPos = _ref4.getPos,
    tableRef = _ref4.tableRef,
    isResizing = _ref4.isResizing,
    pluginInjectionApi = _ref4.pluginInjectionApi,
    tableWrapperHeight = _ref4.tableWrapperHeight,
    isWholeTableInDanger = _ref4.isWholeTableInDanger,
    isTableScalingEnabled = _ref4.isTableScalingEnabled,
    isTableWithFixedColumnWidthsOptionEnabled = _ref4.isTableWithFixedColumnWidthsOptionEnabled,
    isTableAlignmentEnabled = _ref4.isTableAlignmentEnabled,
    shouldUseIncreasedScalingPercent = _ref4.shouldUseIncreasedScalingPercent,
    isCommentEditor = _ref4.isCommentEditor;
  var containerRef = useRef(null);
  var tableWidthRef = useRef(akEditorDefaultLayoutWidth);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    resizing = _useState2[0],
    setIsResizing = _useState2[1];
  var _useSharedPluginState2 = useSharedPluginState(pluginInjectionApi, ['table'], {
      disabled: editorExperiment('platform_editor_usesharedpluginstateselector', true)
    }),
    tableState = _useSharedPluginState2.tableState;

  // isFullWidthModeEnabled
  var isFullWidthModeEnabledSelector = useSharedPluginStateSelector(pluginInjectionApi, 'table.isFullWidthModeEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isFullWidthModeEnabled = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isFullWidthModeEnabledSelector : tableState === null || tableState === void 0 ? void 0 : tableState.isFullWidthModeEnabled;
  var updateContainerHeight = useCallback(function (height) {
    var _containerRef$current;
    // current StickyHeader State is not stable to be fetch.
    // we need to update stickyHeader plugin to make sure state can be
    //    consistently fetch and refactor below
    var stickyHeaders = (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.getElementsByClassName('pm-table-sticky');
    if (!stickyHeaders || stickyHeaders.length < 1) {
      // when starting to drag, we need to keep the original space,
      // -- When sticky header not appear, margin top(24px) and margin bottom(16px), should be 40px,
      //    1px is border width but collapse make it 0.5.
      // -- When sticky header appear, we should add first row height but reduce
      //    collapsed border
      return typeof height === 'number' ? "".concat(height + 40.5, "px") : 'auto';
    } else {
      var _containerRef$current2;
      var stickyHeaderHeight = ((_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.getElementsByTagName('th')[0].getBoundingClientRect().height) || 0;
      return typeof height === 'number' ? "".concat(height + stickyHeaderHeight + 39.5, "px") : 'auto';
    }
  }, []);
  var onResizeStart = useCallback(function () {
    setIsResizing(true);
  }, []);
  var onResizeStop = useCallback(function () {
    setIsResizing(false);
  }, []);
  var updateWidth = useCallback(function (width) {
    if (!containerRef.current) {
      return;
    }

    // make sure during resizing
    // the pm-table-resizer-container width is the same as its child div resizer-item
    // otherwise when resize table from wider to narrower , pm-table-resizer-container stays wider
    // and cause the fabric-editor-popup-scroll-parent to overflow
    if (containerRef.current.style.width !== "".concat(width, "px")) {
      containerRef.current.style.width = "".concat(width, "px");
    }
  }, []);
  var displayGuideline = useCallback(function (guidelines) {
    var _pluginInjectionApi$g, _pluginInjectionApi$g2;
    return (_pluginInjectionApi$g = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$g2 = pluginInjectionApi.guideline) === null || _pluginInjectionApi$g2 === void 0 || (_pluginInjectionApi$g2 = _pluginInjectionApi$g2.actions) === null || _pluginInjectionApi$g2 === void 0 ? void 0 : _pluginInjectionApi$g2.displayGuideline(editorView)({
      guidelines: guidelines
    })) !== null && _pluginInjectionApi$g !== void 0 ? _pluginInjectionApi$g : false;
  }, [pluginInjectionApi, editorView]);
  var attachAnalyticsEvent = useCallback(function (payload) {
    var _pluginInjectionApi$a2;
    return pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a2 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a2 === void 0 ? void 0 : _pluginInjectionApi$a2.actions.attachAnalyticsEvent(payload);
  }, [pluginInjectionApi]);
  var displayGapCursor = useCallback(function (toggle) {
    var _pluginInjectionApi$c, _pluginInjectionApi$c2, _pluginInjectionApi$s;
    return (_pluginInjectionApi$c = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$c2 = pluginInjectionApi.core) === null || _pluginInjectionApi$c2 === void 0 ? void 0 : _pluginInjectionApi$c2.actions.execute(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$s = pluginInjectionApi.selection) === null || _pluginInjectionApi$s === void 0 ? void 0 : _pluginInjectionApi$s.commands.displayGapCursor(toggle))) !== null && _pluginInjectionApi$c !== void 0 ? _pluginInjectionApi$c : false;
  }, [pluginInjectionApi]);
  var tableWidth = getTableContainerWidth(node);
  var _useSharedPluginState3 = useSharedPluginState(pluginInjectionApi, ['editorViewMode'], {
      disabled: editorExperiment('platform_editor_usesharedpluginstateselector', true)
    }),
    editorViewModeState = _useSharedPluginState3.editorViewModeState;

  // mode
  var modeSelector = useSharedPluginStateSelector(pluginInjectionApi, 'editorViewMode.mode', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var mode = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? modeSelector : editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode;
  var responsiveContainerWidth = 0;
  var resizeHandleSpacing = 12;
  // When Full width editor enabled, a Mac OS user can change "ak-editor-content-area" width by
  // updating Settings -> Appearance -> Show scroll bars from "When scrolling" to "Always". It causes
  // issues when viwport width is less than full width Editor's width. To detect avoid them
  // we need to use lineLength to defined responsiveWidth instead of containerWidth
  // (which does not get updated when Mac setting changes) in Full-width editor.
  if (isFullWidthModeEnabled) {
    // When: Show scroll bars -> containerWidth = akEditorGutterPadding * 2 + lineLength;
    // When: Always -> containerWidth = akEditorGutterPadding * 2 + lineLength + scrollbarWidth;
    // scrollbarWidth can vary. Values can be 14, 15, 16 and up to 20px;
    responsiveContainerWidth = isTableScalingEnabled ? lineLength : containerWidth - akEditorGutterPaddingDynamic() * 2 - resizeHandleSpacing;
  } else if (isCommentEditor) {
    responsiveContainerWidth = containerWidth - TABLE_OFFSET_IN_COMMENT_EDITOR;
  } else {
    // 76 is currently an accepted padding value considering the spacing for resizer handle
    // containerWidth = width of a DIV with test id="ak-editor-fp-content-area". It is a parent of
    // a DIV with className="ak-editor-content-area". This DIV has padding left and padding right.
    // padding left = padding right = akEditorGutterPadding = 32
    responsiveContainerWidth = isTableScalingEnabled ? containerWidth - akEditorGutterPaddingDynamic() * 2 : containerWidth - akEditorGutterPaddingDynamic() * 2 - resizeHandleSpacing;
  }
  var width = !node.attrs.width && isCommentEditor ? responsiveContainerWidth : Math.min(tableWidth, responsiveContainerWidth);
  if (!isResizing) {
    tableWidthRef.current = width;
  }
  var maxResizerWidth = isCommentEditor ? responsiveContainerWidth : Math.min(responsiveContainerWidth, TABLE_MAX_WIDTH);
  var tableResizerProps = {
    width: width,
    maxWidth: maxResizerWidth,
    containerWidth: containerWidth,
    lineLength: lineLength,
    updateWidth: updateWidth,
    editorView: editorView,
    getPos: getPos,
    node: node,
    tableRef: tableRef,
    displayGuideline: displayGuideline,
    attachAnalyticsEvent: attachAnalyticsEvent,
    displayGapCursor: displayGapCursor,
    isTableAlignmentEnabled: isTableAlignmentEnabled,
    isFullWidthModeEnabled: isFullWidthModeEnabled,
    isTableScalingEnabled: isTableScalingEnabled,
    isTableWithFixedColumnWidthsOptionEnabled: isTableWithFixedColumnWidthsOptionEnabled,
    isWholeTableInDanger: isWholeTableInDanger,
    shouldUseIncreasedScalingPercent: shouldUseIncreasedScalingPercent,
    pluginInjectionApi: pluginInjectionApi,
    onResizeStart: onResizeStart,
    onResizeStop: onResizeStop,
    isCommentEditor: isCommentEditor
  };
  var isLivePageViewMode = (editorExperiment('platform_editor_usesharedpluginstateselector', true) ? mode : editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode) === 'view';
  return /*#__PURE__*/React.createElement(AlignmentTableContainerWrapper, {
    isTableAlignmentEnabled: isTableAlignmentEnabled,
    node: node,
    pluginInjectionApi: pluginInjectionApi,
    getPos: getPos,
    editorView: editorView
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: tableWidthRef.current,
      height: resizing ? updateContainerHeight(tableWrapperHeight !== null && tableWrapperHeight !== void 0 ? tableWrapperHeight : 'auto') : 'auto',
      position: isLivePageViewMode ? 'relative' : 'unset'
    }
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: ClassName.TABLE_RESIZER_CONTAINER,
    ref: containerRef
  }, fg('platform_editor_live_page_prevent_table_recreation') ? null : isLivePageViewMode ? /*#__PURE__*/React.createElement(InnerContainer
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: className,
    node: node
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: {
      width: 'inherit'
    }
  }, children) :
  /*#__PURE__*/
  // Ignored via go/ees005
  // eslint-disable-next-line react/jsx-props-no-spreading
  React.createElement(TableResizer, tableResizerProps, /*#__PURE__*/React.createElement(InnerContainer, {
    className: className,
    node: node
  }, children)), fg('platform_editor_live_page_prevent_table_recreation') ?
  /*#__PURE__*/
  // Ignored via go/ees005
  // eslint-disable-next-line react/jsx-props-no-spreading
  React.createElement(TableResizer, _extends({}, tableResizerProps, {
    disabled: isLivePageViewMode
  }), /*#__PURE__*/React.createElement(InnerContainer, {
    className: className,
    node: node
  }, children)) : null));
});
export var TableContainer = function TableContainer(_ref5) {
  var children = _ref5.children,
    node = _ref5.node,
    className = _ref5.className,
    _ref5$containerWidth = _ref5.containerWidth,
    editorWidth = _ref5$containerWidth.width,
    lineLength = _ref5$containerWidth.lineLength,
    editorView = _ref5.editorView,
    getPos = _ref5.getPos,
    tableRef = _ref5.tableRef,
    isNested = _ref5.isNested,
    tableWrapperHeight = _ref5.tableWrapperHeight,
    isResizing = _ref5.isResizing,
    pluginInjectionApi = _ref5.pluginInjectionApi,
    isWholeTableInDanger = _ref5.isWholeTableInDanger,
    isTableResizingEnabled = _ref5.isTableResizingEnabled,
    isTableScalingEnabled = _ref5.isTableScalingEnabled,
    isTableWithFixedColumnWidthsOptionEnabled = _ref5.isTableWithFixedColumnWidthsOptionEnabled,
    isTableAlignmentEnabled = _ref5.isTableAlignmentEnabled,
    shouldUseIncreasedScalingPercent = _ref5.shouldUseIncreasedScalingPercent,
    isCommentEditor = _ref5.isCommentEditor,
    isChromelessEditor = _ref5.isChromelessEditor;
  if (isTableResizingEnabled && !isNested) {
    return /*#__PURE__*/React.createElement(ResizableTableContainer
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    , {
      className: className,
      node: node
      // Ignored via go/ees005
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ,
      containerWidth: editorWidth
      // Ignored via go/ees005
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ,
      lineLength: lineLength,
      editorView: editorView,
      getPos: getPos,
      tableRef: tableRef,
      tableWrapperHeight: tableWrapperHeight,
      isResizing: isResizing,
      pluginInjectionApi: pluginInjectionApi,
      isTableScalingEnabled: isTableScalingEnabled,
      isTableWithFixedColumnWidthsOptionEnabled: isTableWithFixedColumnWidthsOptionEnabled,
      isWholeTableInDanger: isWholeTableInDanger,
      isTableAlignmentEnabled: isTableAlignmentEnabled,
      shouldUseIncreasedScalingPercent: shouldUseIncreasedScalingPercent,
      isCommentEditor: isCommentEditor
    }, children);
  }
  var _getPluginState = getPluginState(editorView.state),
    isDragAndDropEnabled = _getPluginState.isDragAndDropEnabled;
  return /*#__PURE__*/React.createElement(InnerContainer, {
    node: node
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: classNames(className, {
      'less-padding': editorWidth < akEditorMobileBreakoutPoint && !isNested && !(isChromelessEditor && isDragAndDropEnabled)
    }),
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      width: 'inherit',
      marginLeft: isChromelessEditor ? 18 : undefined
    }
  }, children);
};